import { toast } from "$global-providers/toast-provider";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  styled,
  TextField,
  Typography,
} from "@material-ui/core";
import { phone } from "phone";
import { FC, useState } from "react";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { useEpocusUpdateUserConsentMutation } from "./__generated__/epocus-settings-operations.gql";
import { SettingsCard, SettingsCardLabel } from "./settings-card";

const IntroText = styled(Typography)({
  fontSize: rem(14),
  color: EPOCUS_TEXT_SECONDARY,
});

const ConsentPrimaryLabel = styled(Typography)({
  fontSize: rem(14),
  color: EPOCUS_TEXT_PRIMARY,
});

const ConsentHelperText = styled(Typography)({
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
  marginLeft: 32,
});

const PHONE_ERROR_MESSAGE =
  "Enter a valid mobile number in international format, e.g. +234…";

export const ConsentCard: FC<{
  userId: string;
  whatsappConsent: boolean;
  marketing: boolean;
  telephone?: string | null;
}> = ({ userId, whatsappConsent, marketing, telephone }) => {
  const [whatsappState, setWhatsappState] = useState(whatsappConsent);
  const [marketingState, setMarketingState] = useState(marketing);
  const [phoneState, setPhoneState] = useState(telephone ?? "");
  const [phoneError, setPhoneError] = useState(false);

  const [updateUser, { loading }] = useEpocusUpdateUserConsentMutation({
    onCompleted: () => toast.success("Communication preferences saved"),
    onError: (e) => toast.error(`Couldn't save: ${e.message}`),
  });

  const isPhoneValid = (value: string) => value === "" || phone(value).isValid;

  const handleWhatsappChange = (checked: boolean) => {
    setWhatsappState(checked);
    if (!checked) {
      setPhoneError(false);
    } else {
      setPhoneError(!isPhoneValid(phoneState) || phoneState === "");
    }
  };

  const handlePhoneChange = (value: string) => {
    setPhoneState(value);
    if (whatsappState) {
      setPhoneError(!isPhoneValid(value) || value === "");
    } else {
      setPhoneError(value !== "" && !isPhoneValid(value));
    }
  };

  const handleSave = async () => {
    const missingRequiredPhone = whatsappState && phoneState === "";
    const invalidPhone = phoneState !== "" && !phone(phoneState).isValid;

    if (missingRequiredPhone || invalidPhone) {
      setPhoneError(true);
      return;
    }

    await updateUser({
      variables: {
        input: {
          _id: userId,
          person: {
            whatsappConsent: whatsappState,
            telephone: phone(phoneState).phoneNumber || phoneState || undefined,
          },
          consent: { marketing: marketingState },
        },
      },
    });
  };

  return (
    <SettingsCard>
      <SettingsCardLabel>Communication consent</SettingsCardLabel>
      <IntroText>
        We use WhatsApp and other means (including SMS and Email) for
        communications about product releases, support, and other important
        information for our users and partners.
      </IntroText>

      <Box display="flex" flexDirection="column">
        <FormControlLabel
          control={
            <Checkbox
              checked={whatsappState}
              onChange={(e) => handleWhatsappChange(e.target.checked)}
              color="primary"
            />
          }
          label={
            <ConsentPrimaryLabel>
              Receive communications over WhatsApp
            </ConsentPrimaryLabel>
          }
        />
        <ConsentHelperText>
          By ticking this box, you confirm you give permission for us to send
          you communications over WhatsApp. A mobile number is required.
        </ConsentHelperText>
      </Box>

      <Box display="flex" flexDirection="column">
        <FormControlLabel
          control={
            <Checkbox
              checked={marketingState}
              onChange={(e) => setMarketingState(e.target.checked)}
              color="primary"
            />
          }
          label={
            <ConsentPrimaryLabel>
              Receive communications via other means (SMS and Email)
            </ConsentPrimaryLabel>
          }
        />
        <ConsentHelperText>
          By ticking this box, you confirm you give permission for us to send
          you communications via other means, including SMS and Email.
        </ConsentHelperText>
      </Box>

      <TextField
        id="epocus-settings-telephone"
        label="Mobile phone number"
        placeholder="+234…"
        value={phoneState}
        onChange={(e) => handlePhoneChange(e.target.value)}
        required={whatsappState}
        error={phoneError}
        helperText={phoneError ? PHONE_ERROR_MESSAGE : undefined}
        variant="outlined"
        fullWidth
      />

      <Button
        variant="contained"
        color="primary"
        onClick={handleSave}
        disabled={loading}
        style={{ alignSelf: "flex-end" }}
      >
        Save
      </Button>
    </SettingsCard>
  );
};
